import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Info } from 'lucide-react';
import { API } from '../../App';
import TabNavigation from '../../components/TabNavigation';
import ContractorPicker from '../../components/ContractorPicker';
import { useSite } from '../../context/SiteContext';
import { useAlert } from '../../context/AlertContext';
import { DateTimePicker } from '../../components/ui/date-picker';
import { formatDate } from '../../utils/dateUtils';
import PeopleMultiSelect from '../../components/PeopleMultiSelect';

const ScheduleAudit = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const preSelectedTemplate = location.state?.template;
  const editSchedule = location.state?.editSchedule;
  const isEditing = !!editSchedule;

  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErrors, setLoadErrors] = useState({});
  const [showAuditorHelp, setShowAuditorHelp] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    template_id: editSchedule?.template_id || preSelectedTemplate?.id || '',
    template_name: editSchedule?.template_name || preSelectedTemplate?.template_name || '',
    audit_title: editSchedule?.audit_title || '',
    auditor_id: editSchedule?.auditor_id || '',
    auditor_name: editSchedule?.auditor_name || '',
    co_auditors: editSchedule?.co_auditors || [],
    scheduled_date: editSchedule?.scheduled_date || '',
    recurrence: editSchedule?.recurrence || '',
    location: editSchedule?.location || '',
    department_id: editSchedule?.department_id || '',
    // Contractor attribution, planned here and inherited by the audit record
    // when the audit is started.
    contractor_company_id: editSchedule?.contractor_company_id || undefined
  });
  const [departments, setDepartments] = useState([]);
  const { activeSiteId } = useSite();

  const recurrenceOptions = ['', 'Weekly', 'Monthly', 'Quarterly', 'Annual'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    // Each request handles its own failure so one error can't blank the whole
    // form silently (a swallowed /users 403 was rendering an empty auditor
    // dropdown with no explanation).
    const [templatesRes, auditorsRes, deptsRes] = await Promise.all([
      axios.get(`${API}/audit-templates`, { headers }).catch(err => ({ error: err })),
      axios.get(`${API}/audit-schedules/eligible-auditors`, { headers }).catch(err => ({ error: err })),
      axios.get(`${API}/departments`, { headers }).catch(() => ({ data: { departments: [] } }))
    ]);

    setDepartments(deptsRes.data?.departments || []);

    if (templatesRes.error) {
      console.error('Failed to fetch audit templates:', templatesRes.error);
      setLoadErrors(prev => ({ ...prev, templates: 'Couldn\'t load audit templates. Please refresh or contact your administrator.' }));
    } else {
      setTemplates(templatesRes.data.filter(t => t.is_active));
    }

    if (auditorsRes.error) {
      console.error('Failed to fetch eligible auditors:', auditorsRes.error);
      setLoadErrors(prev => ({ ...prev, auditors: 'Couldn\'t load eligible auditors. Please refresh or contact your administrator.' }));
    } else {
      // Eligibility (role filtering) is resolved server-side.
      setUsers(auditorsRes.data.auditors || []);
    }

    setLoading(false);
  };

  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    const template = templates.find(t => t.id === templateId);
    
    setScheduleForm({
      ...scheduleForm,
      template_id: templateId,
      template_name: template?.template_name || '',
      audit_title: template ? `${template.template_name} - ${formatDate(new Date())}` : ''
    });
  };

  const handleAuditorChange = (e) => {
    const auditorId = e.target.value;
    const auditor = users.find(u => u.id === auditorId);
    
    setScheduleForm({
      ...scheduleForm,
      auditor_id: auditorId,
      auditor_name: auditor?.name || '',
      // The lead auditor can't also be a co-auditor — drop them if they were
      // already picked, so the form can't submit a combination the API rejects.
      co_auditors: (scheduleForm.co_auditors || []).filter(c => c.user_id !== auditorId)
    });
  };

  const handleSubmit = async () => {
    if (!scheduleForm.template_id || !scheduleForm.auditor_id || !scheduleForm.scheduled_date) {
      showWarning('Please fill in all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

      if (isEditing) {
        await axios.put(`${API}/audit-schedules/${editSchedule.id}`, scheduleForm, { headers });
        showSuccess('Audit schedule updated successfully!');
      } else {
        // REL-18: a schedule belongs to the active site
        await axios.post(
          `${API}/audit-schedules`,
          { ...scheduleForm, site_id: activeSiteId || undefined },
          { headers }
        );
        showSuccess('Audit scheduled successfully!');
      }
      navigate('/audits');
    } catch (err) {
      console.error('Failed to schedule audit:', err);
      const status = err.response?.status;
      const rawDetail = err.response?.data?.detail;
      // 422 details can arrive as Pydantic's array form; only a string is
      // safe to render.
      const detail = typeof rawDetail === 'string' ? rawDetail : null;
      const fallback = isEditing ? 'Failed to update audit schedule' : 'Failed to schedule audit';

      if (status === 409) {
        showWarning(detail || 'A scheduling conflict was detected. Please select a different date or time.');
      } else if (status === 400 || status === 422) {
        // These carry a specific, user-actionable reason (past date, unknown
        // co-auditor, unregistered contractor). Showing the generic message
        // instead left the user with nothing to act on.
        showWarning(detail || fallback);
      } else {
        showError(fallback);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="p-6">
        <div className="mb-6">
          <button
            onClick={() => navigate('/audits')}
            className="text-blue-600 hover:underline mb-4 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Audits Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-800">{isEditing ? '📅 Edit Scheduled Audit' : '📅 Schedule Audit'}</h1>
          <p className="text-gray-600 mt-1">{isEditing ? 'Update the scheduled audit details' : 'Plan a new audit or inspection'}</p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6 max-w-3xl">
          <div className="space-y-6">
            {/* Template Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Audit Template <span className="text-red-500">*</span>
              </label>
              <select
                value={scheduleForm.template_id}
                onChange={handleTemplateChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Select a template</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.template_name} - {template.audit_type}
                  </option>
                ))}
              </select>
              {loadErrors.templates && (
                <p className="mt-1 text-sm text-red-600" role="alert">{loadErrors.templates}</p>
              )}
            </div>

            {/* Audit Title */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Audit Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={scheduleForm.audit_title}
                onChange={(e) => setScheduleForm({...scheduleForm, audit_title: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="e.g., Monthly Site Walk - January 2025"
              />
            </div>

            {/* Auditor Selection */}
            <div>
              <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 mb-2">
                <span>Assign Lead Auditor <span className="text-red-500">*</span></span>
                <span className="relative inline-flex">
                  <button
                    type="button"
                    onClick={() => setShowAuditorHelp(v => !v)}
                    aria-label="Who can be assigned as auditor?"
                    aria-expanded={showAuditorHelp}
                    className="text-gray-400 hover:text-blue-600 focus:text-blue-600 focus:outline-none"
                  >
                    <Info size={16} />
                  </button>
                  {showAuditorHelp && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowAuditorHelp(false)}
                        aria-hidden="true"
                      />
                      <div
                        role="dialog"
                        className="absolute left-6 top-0 z-50 w-80 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs font-normal text-gray-700"
                      >
                        <p className="font-semibold text-gray-800 mb-1">Who appears in this list?</p>
                        <p className="mb-2">
                          Only users with one of these roles can be assigned as an auditor:
                        </p>
                        <ul className="list-disc pl-4 mb-2 space-y-0.5">
                          <li>External Auditor</li>
                          <li>Safety Officer</li>
                          <li>Site Admin</li>
                          <li>Site Head</li>
                        </ul>
                        <p className="text-gray-600">
                          If the list is empty, ask an admin to assign one of these roles to the user
                          in <span className="font-medium">Admin &rarr; Users</span>.
                        </p>
                      </div>
                    </>
                  )}
                </span>
              </label>
              <select
                value={scheduleForm.auditor_id}
                onChange={handleAuditorChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">
                  {loadErrors.auditors
                    ? 'Auditor list unavailable'
                    : users.length === 0 ? 'No eligible auditors found' : 'Select an auditor'}
                </option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.name} - {u.role_label}
                  </option>
                ))}
              </select>
              {loadErrors.auditors && (
                <p className="mt-1 text-sm text-red-600" role="alert">{loadErrors.auditors}</p>
              )}
            </div>

            {/* Co-Auditors / Joint Auditors — optional, may be more than one.
                Planned here so the team pre-fills when the audit is conducted. */}
            <PeopleMultiSelect
              label="Co-Auditors / Joint Auditors"
              helpText="Optional. Add joint auditors from your organisation, or external auditors (e.g. certification body) who have no login."
              value={scheduleForm.co_auditors}
              onChange={(next) => setScheduleForm({ ...scheduleForm, co_auditors: next })}
              options={users}
              excludeIds={scheduleForm.auditor_id ? [scheduleForm.auditor_id] : []}
              allowExternal
              placeholder="Search eligible auditors — pick multiple"
            />

            <div className="grid grid-cols-2 gap-4">
              {/* Scheduled Date */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Scheduled Date & Time <span className="text-red-500">*</span>
                </label>
                <DateTimePicker
                  value={scheduleForm.scheduled_date}
                  onChange={(value) => setScheduleForm({...scheduleForm, scheduled_date: value})}
                  placeholder="Select date and time"
                  minDate={new Date()}
                />
              </div>

              {/* Recurrence */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Recurrence
                </label>
                <select
                  value={scheduleForm.recurrence}
                  onChange={(e) => setScheduleForm({...scheduleForm, recurrence: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  {recurrenceOptions.map(opt => (
                    <option key={opt} value={opt}>
                      {opt || 'One-time (No recurrence)'}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Location */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Location
                </label>
                <input
                  type="text"
                  value={scheduleForm.location}
                  onChange={(e) => setScheduleForm({...scheduleForm, location: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., Building A, Floor 3"
                />
              </div>

              {/* Department */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Department
                </label>
                <select
                  value={scheduleForm.department_id}
                  onChange={(e) => setScheduleForm({...scheduleForm, department_id: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">— Select department —</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Info Box */}
            {scheduleForm.recurrence && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>ℹ️ Recurring Audit:</strong> After each scheduled date passes, the next {scheduleForm.recurrence.toLowerCase()} occurrence is created automatically and the assigned auditor is notified. Cancelling an occurrence stops the recurrence.
                </p>
              </div>
            )}

            {/* Contractor attribution. A contractor audit; optional and
                collapsed for the common case. Set here rather than on the audit
                record because the record is created FROM this schedule, so
                attribution is planned once and carried forward — the same way
                the co-auditor team already is. */}
            <ContractorPicker
              contractorId={scheduleForm.contractor_company_id}
              showWorker={false}
              siteId={activeSiteId}
              onChange={(cid) => setScheduleForm((prev) => ({
                ...prev, contractor_company_id: cid || undefined,
              }))}
              hint="Links this audit to the contractor being audited, so it appears in their EHS history."
            />

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={() => navigate('/audits')}
                className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!scheduleForm.template_id || !scheduleForm.auditor_id || !scheduleForm.scheduled_date}
                className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isEditing ? 'Update Schedule' : 'Schedule Audit'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
      </div>
  );
};

export default ScheduleAudit;
